import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useFilterContext } from '../../contexts/FilterContext';

/**
 * This blacklist contains a list of predicates that define invalid filter combinations.
 * To add new filter context combinations, add a new entry with, at minimum, a predicate function.
 * For example, if you want to blacklist the combination of Active Status = 'active' and Game Type = 'regular season',
 * you would add a predicate as such:
 * { predicate: ctx => ctx.activeStatus === 'active' && ctx.gameTypeFilters.reg === true, message: "You cannot use active players in regular season games" }
 * The message field is optional; if not provided, the default message will be used.
 */
const BLACKLIST: Array<{
    predicate: (context: Record<string, any>) => boolean;
    message?: string;
}> = [
        {
            predicate: ctx => ctx.halfQuarterStatus !== 'none' && Array.isArray(ctx.statsFilterChips) && ctx.statsFilterChips.some((item: string) => item.includes('(+/-)')),
        },
    ];

const DEFAULT_MESSAGE = "Oops! It looks like there is a problem with the combination of filters you are using. Please contact us for assistance: support@esb.com.";

function getBlacklistMessage(context: Record<string, any>): string | null {
    const entry = BLACKLIST.find(({ predicate }) => predicate(context));
    return entry && (entry.message ?? DEFAULT_MESSAGE) || null;
}

function isBlacklisted(context: Record<string, any>): boolean {
    return BLACKLIST.some(({ predicate }) => predicate(context));
}

function FilterBlacklistWarning() {
    const filterContext: any = useFilterContext();
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    useEffect(() => {
        setWarningMessage(getBlacklistMessage(filterContext));
    }, [filterContext]);

    if (!warningMessage) return null;

    return (
        <Box sx={{ marginY: 2 }}>
            <Alert severity="warning">
                <Typography variant="body1" fontWeight="fontWeightBold">
                    {warningMessage}
                </Typography>
            </Alert>
        </Box>
    );
}

export { isBlacklisted };
export default FilterBlacklistWarning;
